import React, { useState, useEffect, useMemo, useRef } from 'react';
import config from '../utils/config'
import PressureAnalysisDisplay from './PressureAnalysisDisplay';
import axiosInstance from '../utils/axiosInstance';
import FilterDateSelector from './FilterDateSelector';
import {
  Device,
  AnalysisResults,
  AnalysisRebuildStatus,
  AnalysisMetadataResponse,
  CompareOrientation,
  DownloadCSVRequest,
  DownloadCSVResponse,
  PinnedAnalysis,
  AvailableAnalysis,
} from '../types';
import { isLocal } from '../utils/isLocal';
import { useDevices } from '../hooks/useDevices';
import { newerRebuild } from '../hooks/useAnalysisRebuildStatus';
import { useFirestoreEnrichment } from '../hooks/useFirestoreEnrichment';
import DeviceSelector from './DeviceSelector';
import ClipAnalysisMethodsDialog, { type ClipAnalysisMethods } from './ClipAnalysisMethodsDialog';
import RequestedAnalysisComparison from './RequestedAnalysisComparison';
import ClipAnalysisRebuilds, { type ClipMethodRebuild } from './ClipAnalysisRebuilds';
import type { ClipAnalysisSaveResult } from './ClipAnalysisMethodsDialog';
import OpenAlertsBox from './DeviceWorkspace/OpenAlertsBox';
import { deviceClipLabel } from '../utils/clipLabel';
import { pinCandidateGroups } from '../utils/pinCandidates';
import { readCompareOrientation, writeCompareOrientation } from '../utils/compareOrientation';
import { useAuth } from '../context/AuthContext';
import Button from './ui/Button';
import { Input } from './pilot-ui/input';
import { Checkbox } from './pilot-ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './pilot-ui/select';
import {
  formatApiDateForInput,
  isValidDateRange,
  normalizeDateRange,
  resolveDateRange,
  type DateRange,
  type DateRangeBounds,
} from '../utils/dateRange';

const yesterday = () => {
  const yesterday = new Date(new Date().setHours(0, 0, 0, 0));
  yesterday.setDate(yesterday.getDate() - 1);
  return yesterday.toLocaleDateString('en-CA');
}

const getAnalysisMetadata = async (
  deviceName: string,
  analysisKey?: string,
): Promise<AnalysisMetadataResponse> => {
  const lambdaUrl = `${config.fetchDailyApiUrl}/${encodeURIComponent(deviceName)}`;
  const params: Record<string, string> = { mode: 'metadata' };
  if (analysisKey) params.analysis_key = analysisKey;
  const response = await axiosInstance.get<AnalysisMetadataResponse>(lambdaUrl, { params });
  return response.data;
};

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

const boundsFromMetadata = (metadata: AnalysisMetadataResponse): DateRangeBounds => ({
  oldest: formatApiDateForInput(metadata.oldest_reading_date),
  latest: formatApiDateForInput(metadata.latest_processed_date),
  availableDates: metadata.available_dates ?? null,
});

// Palette for pinned-analysis chart colors. Index is assigned by pin order.
// Keep the first entry blue so single-device mode matches the historical look.
const PIN_COLORS = ['#3b82f6', '#f97316', '#22c55e', '#ef4444', '#a855f7'];
const pinColor = (index: number) => PIN_COLORS[index % PIN_COLORS.length];

const downloadProgressPercent = (processedDays: number, totalDays: number) => {
  if (totalDays <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((processedDays / totalDays) * 100)));
};

const downloadStageLabel = (stage: DownloadCSVResponse['stage'], status: DownloadCSVResponse['status']) => {
  if (status === 'pending' || stage === 'queued') return 'Queueing export';
  if (stage === 'archive') return 'Fetching archived days';
  if (stage === 'database') return 'Appending recent database days';
  if (stage === 'upload') return 'Uploading CSV';
  if (stage === 'complete') return 'Download ready';
  return 'Preparing CSV';
};

interface AnalyzePanelProps {
  /**
   * Plan 94: when rendered inside the device workspace, the route device seeds the
   * initial/primary selection. The panel keeps its own selector so the multi-device
   * Pin-Analysis comparison (Plan 80/31) still works. Omitted on the legacy
   * `/analyze` route, where the operator picks the first device manually.
   */
  device?: Device;
}

const AnalyzePanel: React.FC<AnalyzePanelProps> = ({ device }) => {
  const { devices } = useDevices();
  const { enrichmentMap } = useFirestoreEnrichment();
  const { hasPermission } = useAuth();
  const canManageAnalyses = hasPermission('manage_analysis_runs');
  const [methodsDialogOpen, setMethodsDialogOpen] = useState(false);
  const [methodsResult, setMethodsResult] = useState('');
  const [methodRebuilds, setMethodRebuilds] = useState<{ deviceId: number; methods: ClipMethodRebuild[] } | null>(null);
  const [periodRangeReady, setPeriodRangeReady] = useState<string | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<typeof devices[0] | undefined>(undefined);
  // The dates that drive the inputs and requests are DERIVED, not stored.
  // `requestedRange` is what the user (or FilterDateSelector's auto-select)
  // asked for — null means "no explicit choice", which defaults to the full
  // processed window. `bounds` is the processed-day window from analysis
  // metadata — null while unknown. resolveDateRange() clamps and snaps the
  // request into the window on every render, so the two async responses
  // (filter periods vs metadata) can land in either order without stranding
  // an out-of-window date on the form.
  const [requestedRange, setRequestedRange] = useState<DateRange | null>(null);
  const [bounds, setBounds] = useState<DateRangeBounds | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingOldestDate, setLoadingOldestDate] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({
    processedDays: 0,
    totalDays: 0,
    status: 'pending' as DownloadCSVResponse['status'],
    stage: 'queued' as DownloadCSVResponse['stage'],
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [pinnedAnalyses, setPinnedAnalyses] = useState<PinnedAnalysis[]>([]);
  // Plan 80: selected analysis for the current device + the device's options.
  const [availableAnalyses, setAvailableAnalyses] = useState<AvailableAnalysis[]>([]);
  const defaultAnalysisKey = availableAnalyses.find(a => a.side_effects?.includes('daily_filter_health_metrics'))?.analysis_key
    ?? availableAnalyses.find(a => a.is_default)?.analysis_key;
  const [metadataLoaded, setMetadataLoaded] = useState(false);
  const [selectedAnalysisKey, setSelectedAnalysisKey] = useState<string>('standard_daily_v1');
  const [isPinMode, setIsPinMode] = useState(false);
  const [compareOrientation, setCompareOrientation] = useState<CompareOrientation>(readCompareOrientation);
  const [isFilterPeriod, setIsFilterPeriod] = useState(false);
  // FS-508: the epoch_id of the selected filter period, or null when the range
  // came from a quick range, a manual date edit, or nothing at all. It is what
  // tells fetch_analysis which filter to name and link — without it a
  // historical period was charted against the CURRENT filter's identity.
  const [selectedEpochId, setSelectedEpochId] = useState<string | null>(null);
  // A filter period with no processed days yet — normally the current filter
  // in the days right after a change. Its dates sit past the processed
  // window, and resolveDateRange would replace a range entirely outside the
  // window with the WHOLE window: the page then charted every earlier filter
  // under the new filter's name. Such a period keeps its own dates and runs
  // to empty charts; the raw scatter still reads its readings.
  const [periodHasData, setPeriodHasData] = useState(true);
  const emptyPeriodSelected = isFilterPeriod && !periodHasData;

  // Dates lock once the user has committed at least one analysis under pin mode.
  const datesLocked = isPinMode && pinnedAnalyses.length > 0;

  // Plan 67b follow-up: when fetch_analysis returns 200 with empty arrays
  // (no daily_calcs yet — typically a freshly installed device that hasn't
  // had a full process_daily_data cycle), surface a friendly empty-state
  // banner instead of just rendering blank charts.
  const emptyAnalyses = pinnedAnalyses.filter(
    (p) => (p.results.chart_data?.dates?.length ?? 0) === 0,
  );

  const deviceFromName = (name: string) => devices.find((device) => device.name === name);

  // The effective range shown and submitted. Under pin-mode lock the
  // materialized request is frozen as-is (a locked range may deliberately sit
  // outside the current device's window); otherwise it re-derives whenever
  // the request or the window changes.
  const { startDate, endDate } = useMemo(() => {
    if ((datesLocked || emptyPeriodSelected) && requestedRange) {
      return normalizeDateRange(requestedRange.startDate, requestedRange.endDate);
    }
    return resolveDateRange(requestedRange, bounds, yesterday());
  }, [requestedRange, bounds, datesLocked, emptyPeriodSelected]);

  const oldestDate = bounds?.oldest ?? '';
  const latestDate = bounds?.latest ?? '';
  const availableDates = bounds?.availableDates ?? null;

  const handleDateRangeSelect = (
    start: string,
    end: string,
    epochId: string | null = null,
    hasProcessedDays = true,
  ) => {
    setRequestedRange(normalizeDateRange(start, end));
    setIsFilterPeriod(true);
    setSelectedEpochId(epochId);
    setPeriodHasData(hasProcessedDays);
  };

  // A hand-edited date is no longer a period, so the filter identity goes back
  // to the device's own. The dates alone cannot say which period they meant.
  const handleStartDateChange = (value: string) => {
    setRequestedRange(normalizeDateRange(value, endDate, 'start'));
    setIsFilterPeriod(false);
    setSelectedEpochId(null);
  };

  const handleEndDateChange = (value: string) => {
    setRequestedRange(normalizeDateRange(startDate, value, 'end'));
    setIsFilterPeriod(false);
    setSelectedEpochId(null);
  };

  // Metadata responses can arrive out of order across device/analysis
  // switches — only the newest request may write bounds.
  const boundsRequestIdRef = useRef(0);
  const analysisGenerationRef = useRef(0);
  const completionRefreshRef = useRef(0);
  const completionLifetimeRef = useRef(0);
  useEffect(() => () => { completionLifetimeRef.current += 1; }, []);
  const routePeriodRef = useRef<{
    deviceId: number;
    epochId: string | null;
    hasData: boolean;
    startDate: string;
    endDate: string;
  } | null>(null);

  const selectDeviceByName = async (
    device_name: string,
    { analysisKey, preserveRange = false }: { analysisKey?: string; preserveRange?: boolean } = {},
  ) => {
    const device = deviceFromName(device_name);
    setSelectedDevice(device);
    setMethodsResult('');
    setMethodRebuilds(null);
    setPeriodRangeReady(null);
    // FS-508: epoch ids belong to one device. Carrying the old device's
    // selection into the new one would ask for another device's filter, which
    // the lambda rejects; the selector re-selects and re-supplies it.
    setSelectedEpochId(null);
    setPeriodHasData(true);
    if (!isPinMode) {
      setPinnedAnalyses([]);
    }
    setSuccess(false);
    setError('');
    // Pin mode carries the locked range across device switches; otherwise
    // the new device starts with no request — full history by default until
    // FilterDateSelector auto-selects the newest period.
    if (!datesLocked && !preserveRange) {
      setRequestedRange(null);
    }
    const requestId = ++boundsRequestIdRef.current;
    // Clear the previous device's window immediately so nothing clamps or
    // snaps against the wrong device while metadata is in flight.
    setBounds(null);
    setMetadataLoaded(false);

    if (device_name) {
      setLoadingOldestDate(true);
      try {
        const metadata = await getAnalysisMetadata(device_name, analysisKey);
        if (requestId !== boundsRequestIdRef.current) return;
        // Plan 80: capture the device's analyses + default selection.
        setAvailableAnalyses(metadata.available_analyses ?? []);
        setSelectedAnalysisKey(analysisKey ?? metadata.analysis_key ?? 'standard_daily_v1');
        setBounds(boundsFromMetadata(metadata));
        setMetadataLoaded(true);
      } catch (error) {
        if (requestId !== boundsRequestIdRef.current) return;
        console.error('Error fetching oldest date:', error);
        setError('Failed to fetch oldest reading date');
      } finally {
        if (requestId === boundsRequestIdRef.current) {
          setLoadingOldestDate(false);
        }
      }
    } else {
      setAvailableAnalyses([]);
      setMetadataLoaded(false);
      setSelectedAnalysisKey('standard_daily_v1');
      // A superseded in-flight fetch skips its own loading-clear (request-id
      // mismatch), so deselecting mid-fetch must clear it here or the form
      // sticks on "Loading...".
      setLoadingOldestDate(false);
    }
  };

  const handleDeviceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    selectDeviceByName(e.target.value);
  };

  // Plan 80: switching analysis refreshes per-analysis processed-date metadata —
  // each analysis has its own daily_calcs history, so the available window
  // differs (e.g. an alternate analysis backfilled over a shorter span than
  // standard). Writing bounds is enough: the derived range re-clamps itself.
  // Skipped when dates are locked (pin mode). The old window stays up while
  // the new one loads — windows usually overlap, and a null flash would reset
  // the inputs to yesterday.
  const handleAnalysisChange = async (key: string) => {
    setSelectedAnalysisKey(key);
    if (!selectedDevice?.name || datesLocked) return;
    const requestId = ++boundsRequestIdRef.current;
    setError('');
    try {
      const metadata = await getAnalysisMetadata(selectedDevice.name, key);
      if (requestId !== boundsRequestIdRef.current) return;
      setBounds(boundsFromMetadata(metadata));
    } catch (err) {
      if (requestId !== boundsRequestIdRef.current) return;
      console.error('Error fetching analysis metadata:', err);
      setError('Could not load the date range for this analysis. Choose another method, then return to this one to retry.');
    }
  };

  // Plan 94: seed the primary selection from the workspace route device. Re-seeds
  // when the workspace switches devices (device.id changes). Replaces the retired
  // Plan 89 cross-page pinned-device auto-select.
  const seededRef = useRef<number | null>(null);
  useEffect(() => {
    if (!device || devices.length === 0) return;
    if (seededRef.current === device.id) return;
    const match = devices.find((d) => d.id === device.id);
    if (match) {
      seededRef.current = device.id;
      selectDeviceByName(match.name);
    }
    // selectDeviceByName is recreated each render; the ref guard makes this run
    // once per route device.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device, devices]);

  const rememberRoutePeriod = () => {
    if (!selectedDevice) return;
    routePeriodRef.current = {
      deviceId: selectedDevice.id,
      epochId: selectedEpochId,
      hasData: periodHasData,
      startDate,
      endDate,
    };
  };

  const handleTogglePinMode = (checked: boolean) => {
    if (checked) rememberRoutePeriod();
    setIsPinMode(checked);
    if (!checked) {
      // Keep only the latest route-Clip result for the selected method.
      // Pending comparisons still belong to the pin-mode session being left.
      analysisGenerationRef.current += 1;
      setLoading(false);
      const routeDevice = device ?? selectedDevice;
      setPinnedAnalyses((prev) => prev.filter((pin) => (
        pin.device.id === routeDevice?.id && pin.analysisKey === selectedAnalysisKey
      )).slice(-1));
      if (routeDevice && routeDevice.id !== selectedDevice?.id) {
        void selectDeviceByName(routeDevice.name, { analysisKey: selectedAnalysisKey, preserveRange: true });
        const period = routePeriodRef.current;
        // Epochs belong to Clips; restore the route period only while its
        // dates still match the shared comparison window.
        if (isFilterPeriod && period?.deviceId === routeDevice.id
          && period.startDate === startDate && period.endDate === endDate) {
          setSelectedEpochId(period.epochId);
          setPeriodHasData(period.hasData);
        }
      }
      setCompareOrientation(readCompareOrientation());
    }
  };

  // Plan 80: pins are identified by (device.id, analysisKey).
  const handleUnpin = (deviceId: number, analysisKey: string) => {
    setPinnedAnalyses((prev) =>
      prev.filter((p) => !(p.device.id === deviceId && p.analysisKey === analysisKey)));
  };

  const handleDownloadCSV = async () => {
    setError('');
    setDownloadingCsv(true);
    if (!selectedDevice?.name) {
      setDownloadingCsv(false);
      return;
    }
    if (!isValidDateRange(startDate, endDate)) {
      setError('Start date must be on or before end date');
      setDownloadingCsv(false);
      return;
    }

    try {
      if (!config.downloadCsvApiUrl) {
        throw new Error('CSV download endpoint is not configured');
      }

      const downloadRequest: DownloadCSVRequest = {
        job_id: window.crypto?.randomUUID?.(),
        device_name: selectedDevice.name,
        start_date: startDate,
        end_date: endDate
      };

      const startResponse = await axiosInstance.post<DownloadCSVResponse>(
        config.downloadCsvApiUrl!,
        downloadRequest,
        { timeout: 300000 }
      );

      const jobId = startResponse.data?.job_id;
      if (!jobId) {
        throw new Error('CSV export job was not created');
      }
      setDownloadProgress({
        processedDays: startResponse.data?.processed_days ?? 0,
        totalDays: startResponse.data?.total_days ?? 0,
        status: startResponse.data?.status ?? 'pending',
        stage: startResponse.data?.stage ?? 'queued',
      });

      let statusResponse: DownloadCSVResponse | null = null;
      for (let attempt = 0; attempt < 90; attempt += 1) {
        await wait(2000);
        const pollResponse = await axiosInstance.get<DownloadCSVResponse>(
          `${config.downloadCsvApiUrl}/${encodeURIComponent(jobId)}`,
          { timeout: 30000 }
        );
        statusResponse = pollResponse.data;
        setDownloadProgress({
          processedDays: statusResponse.processed_days ?? 0,
          totalDays: statusResponse.total_days ?? 0,
          status: statusResponse.status,
          stage: statusResponse.stage ?? 'queued',
        });

        if (statusResponse.status === 'complete') {
          break;
        }
        if (statusResponse.status === 'failed') {
          throw new Error(statusResponse.error || 'CSV export failed');
        }
      }

      if (!statusResponse || statusResponse.status !== 'complete') {
        throw new Error('CSV export timed out');
      }

      if (!statusResponse.download_url || !statusResponse.filename) {
        throw new Error('CSV download link was not returned');
      }

      const link = document.createElement('a');
      link.href = statusResponse.download_url;
      link.setAttribute('download', statusResponse.filename);
      link.setAttribute('rel', 'noopener');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading CSV:', error);
      setError('Failed to download CSV');
    } finally {
      setDownloadingCsv(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!selectedDevice?.name) {
      setError('Please select a device');
      return;
    }

    if (!isValidDateRange(startDate, endDate)) {
      setError('Start date must be on or before end date');
      return;
    }

    if (isPinMode && pinnedAnalyses.some(
      (p) => p.device.id === selectedDevice.id && p.analysisKey === selectedAnalysisKey)) {
      const lbl = availableAnalyses.find((a) => a.analysis_key === selectedAnalysisKey)?.display_name
        ?? selectedAnalysisKey;
      setError(`${selectedDevice.name} (${lbl}) is already pinned`);
      return;
    }

    const generation = analysisGenerationRef.current;
    setLoading(true);
    if (!isPinMode) {
      setPinnedAnalyses([]);
    }
    // Materialize the derived range as the request, so a pin-mode lock
    // freezes exactly the dates this analysis ran with — even when the user
    // never touched the date inputs and the range was pure default.
    setRequestedRange({ startDate, endDate });

    try {
      const lambdaUrl = `${config.fetchDailyApiUrl}/${encodeURIComponent(selectedDevice.name)}`;
      const params: Record<string, string> = {
        start_date: startDate,
        end_date: endDate,
        agg: 'median',
        analysis_key: selectedAnalysisKey,
      };
      // FS-508: only when a filter period is selected. Omitted, the response is
      // the pre-FS-508 one, which is what a quick range and a hand-typed range
      // still want.
      if (selectedEpochId) params.epoch_id = selectedEpochId;
      const response = await axiosInstance.get<AnalysisResults>(lambdaUrl, { params });
      if (generation !== analysisGenerationRef.current) return;
      if (!response) {
        throw new Error('Analysis failed');
      }
      const resolvedKey = response.data.analysis_key ?? selectedAnalysisKey;
      const analysisLabel = response.data.selected_analysis?.display_name
        ?? availableAnalyses.find((a) => a.analysis_key === resolvedKey)?.display_name
        ?? resolvedKey;
      const entry: PinnedAnalysis = {
        device: selectedDevice,
        results: response.data,
        color: pinColor(isPinMode ? pinnedAnalyses.length : 0),
        analysisKey: resolvedKey,
        analysisLabel,
        // FS-723: the processed window this run was bounded by.
        latestProcessedDate: latestDate || undefined,
      };
      setPinnedAnalyses((prev) => (isPinMode ? [...prev, entry] : [entry]));
      setSuccess(true);
    } catch (err: unknown) {
      if (generation !== analysisGenerationRef.current) return;
      const axiosError = err as { response?: { data?: { error?: string } } };
      if (axiosError?.response?.data?.error) {
        const formattedError = axiosError.response.data.error.replace(/\n\s*\n/g, '\n');
        setError(`Analysis failed: ${formattedError}`);
      } else {
        setError('Analysis failed. Please try again.');
      }
      console.error('Error during analysis:', err);
    } finally {
      if (generation === analysisGenerationRef.current) setLoading(false);
    }
  };
  // Fetch one pin on the current date window or an explicitly refreshed range.
  const fetchPin = async (deviceName: string, analysisKey: string, color: string,
    range: DateRange = { startDate, endDate }): Promise<PinnedAnalysis | null> => {
    const device = deviceFromName(deviceName);
    if (!device) return null;
    const lambdaUrl = `${config.fetchDailyApiUrl}/${encodeURIComponent(deviceName)}`;
    const params: Record<string, string> = {
      start_date: range.startDate, end_date: range.endDate, agg: 'median', analysis_key: analysisKey,
    };
    // FS-508: the epoch belongs to whichever device the dropdown is showing.
    // "Open comparison" pins a device chosen from the Analysis Methods table,
    // which is often NOT that one — sending its epoch would ask for another
    // device's filter and the lambda rejects it outright.
    if (selectedEpochId && deviceName === selectedDevice?.name) {
      params.epoch_id = selectedEpochId;
    }
    const response = await axiosInstance.get<AnalysisResults>(lambdaUrl, { params });
    const resolvedKey = response.data.analysis_key ?? analysisKey;
    const analysisLabel = response.data.selected_analysis?.display_name ?? resolvedKey;
    // FS-723: the loaded window belongs to the selected device and analysis
    // only; any other pin leaves it unknown and its scatter keeps the range.
    const latestProcessedDate = deviceName === selectedDevice?.name && analysisKey === selectedAnalysisKey
      ? latestDate || undefined
      : undefined;
    return { device, results: response.data, color, analysisKey: resolvedKey, analysisLabel, latestProcessedDate };
  };

  const handleOpenComparison = async (analysisKey: string) => {
    if (!selectedDevice) return;
    if (!isPinMode) rememberRoutePeriod();
    const generation = analysisGenerationRef.current;
    const requestId = boundsRequestIdRef.current;
    const method = availableAnalyses.find(a => a.analysis_key === analysisKey);
    if (!method) {
      let name = analysisKey;
      try {
        const { data } = await axiosInstance.get<ClipAnalysisMethods>(
          `${config.devicesApiUrl}/analysis-methods/devices/${selectedDevice.id}`);
        name = data.methods.find(m => m.analysis_key === analysisKey)?.display_name ?? analysisKey;
      } catch (err) {
        console.error('Could not load the requested comparison method name:', err);
      }
      if (requestId === boundsRequestIdRef.current) setError(`${name} is not turned on for this Clip`);
      return;
    }
    setError('');
    setIsPinMode(true);
    setCompareOrientation('overlaid');
    setRequestedRange({ startDate, endDate });
    try {
      const keys = [...new Set(defaultAnalysisKey ? [defaultAnalysisKey, analysisKey] : [analysisKey])];
      const pins = (await Promise.all(keys.map((key, index) =>
        fetchPin(selectedDevice.name, key, pinColor(index)))))
        .filter((p): p is PinnedAnalysis => p !== null);
      if (generation !== analysisGenerationRef.current || requestId !== boundsRequestIdRef.current) return;
      setPinnedAnalyses(pins);
      setSuccess(true);
    } catch (err) {
      if (generation !== analysisGenerationRef.current) return;
      console.error('open comparison', err);
      setError('Failed to open comparison');
    }
  };

  const handleRebuildCompleted = async (analysisKey: string, finished: AnalysisRebuildStatus) => {
    if (!selectedDevice) return;
    const generation = analysisGenerationRef.current;
    const requestId = boundsRequestIdRef.current;
    const lifetime = completionLifetimeRef.current;
    const refresh = ++completionRefreshRef.current;
    const current = () => generation === analysisGenerationRef.current
      && requestId === boundsRequestIdRef.current && lifetime === completionLifetimeRef.current;
    const visible = pinnedAnalyses.filter(pin => pin.device.id === selectedDevice.id && pin.analysisKey === analysisKey);
    try {
      const { data } = await axiosInstance.get<ClipAnalysisMethods>(
        `${config.devicesApiUrl}/analysis-methods/devices/${selectedDevice.id}`);
      if (!current()) return;
      const latest = data.methods.find(method => method.analysis_key === analysisKey)?.rebuild;
      if (latest && newerRebuild(latest, finished)) return latest;
      const metadata = await getAnalysisMetadata(selectedDevice.name, selectedAnalysisKey);
      if (!current()) return;
      const nextBounds = boundsFromMetadata(metadata);
      const range = (datesLocked || emptyPeriodSelected) && requestedRange
        ? { startDate, endDate } : resolveDateRange(requestedRange, nextBounds, yesterday());
      const refreshed = await Promise.all(visible.map(async pin => ({ pin,
        updated: await fetchPin(pin.device.name, pin.analysisKey, pin.color, range) })));
      if (!current()) return;
      if (refresh === completionRefreshRef.current) {
        setAvailableAnalyses(metadata.available_analyses ?? []);
        setBounds(nextBounds);
      }
      setPinnedAnalyses(pins => pins.map(pin => {
        const replacement = refreshed.find(item => item.pin === pin)?.updated;
        return replacement ? { ...pin, results: replacement.results,
          latestProcessedDate: pin.analysisKey === selectedAnalysisKey
            ? formatApiDateForInput(metadata.latest_processed_date) || undefined : pin.latestProcessedDate } : pin;
      }));
      return latest;
    } catch (failure) {
      if (current()) {
        console.error('refresh completed analysis rebuild', failure);
        setError('The rebuild completed, but the analysis could not be refreshed. Reload to see the latest results.');
      }
    }
  };

  const handleMethodsSaved = async (message: string, result: ClipAnalysisSaveResult) => {
    setMethodsDialogOpen(false);
    setMethodsResult(message);
    if (!selectedDevice) return;
    setMethodRebuilds({ deviceId: selectedDevice.id,
      methods: result.methods.filter(method => method.enabled && method.rebuild.requested_at !== null) });
    setLoading(true);
    const requestId = ++boundsRequestIdRef.current;
    try {
      const metadata = await getAnalysisMetadata(selectedDevice.name);
      if (requestId !== boundsRequestIdRef.current) return;
      const key = metadata.analysis_key ?? selectedAnalysisKey;
      setAvailableAnalyses(metadata.available_analyses ?? []);
      setSelectedAnalysisKey(key);
      setBounds(boundsFromMetadata(metadata));
      const refreshed = await fetchPin(selectedDevice.name, key, pinColor(0));
      if (requestId === boundsRequestIdRef.current && refreshed) {
        setPinnedAnalyses([{ ...refreshed,
          latestProcessedDate: formatApiDateForInput(metadata.latest_processed_date) || undefined }]);
      }
    } catch (err) {
      console.error('refresh analysis methods', err);
      setError('Analysis methods are stored, but the analysis could not be refreshed. Reload to see the latest results.');
    } finally {
      if (requestId === boundsRequestIdRef.current) setLoading(false);
    }
  };

  const isFormDisabled = loading || loadingOldestDate || downloadingCsv;
  // An empty period's dates are past the processed window by definition, so
  // the picker's own max must not reject them.
  const maxSelectableDate = emptyPeriodSelected && endDate > (latestDate || yesterday())
    ? endDate
    : latestDate || yesterday();
  // The range must be ordered AND, when the device has processed days,
  // contain at least one of them — otherwise the analysis request comes back
  // empty and reads as a failure. Pin mode can carry a locked range onto a
  // device with no data in it; that disables submission.
  // A device with NO processed days yet (empty list — a fresh install) runs
  // anyway: the Scatter Plot and Temperature Plot sections read straight
  // from the readings endpoints, not daily_calcs, and they are the one
  // raw-readings view here — a new install is where it matters. The
  // lambda's 200-with-empty-charts response plus the emptyAnalyses banner
  // below is the designed empty state for that run.
  // An unknown list (null) is permissive so an older lambda response can't
  // brick the form.
  const rangeHasData = availableDates === null
    || availableDates.length === 0
    || emptyPeriodSelected
    || availableDates.some((d) => d >= startDate && d <= endDate);
  const dateRangeValid = isValidDateRange(startDate, endDate)
    && (loadingOldestDate || !selectedDevice || rangeHasData);
  // Keep the route Clip as the comparison anchor while the selector moves
  // between Clips. The detail response can lack the list's flat app keys.
  const currentClip = device
    ? devices.find((candidate) => candidate.id === device.id) ?? device
    : selectedDevice;
  const pinnedIds = new Set(pinnedAnalyses
    .filter((pin) => pin.analysisKey === selectedAnalysisKey)
    .map((pin) => pin.device.id));
  const candidateGroups = pinCandidateGroups(currentClip, devices, pinnedIds);
  const availableDevices = candidateGroups.flat();
  const separatorBeforeIds = new Set(candidateGroups.slice(1).map((group) => group[0].id));
  const progressPercent = downloadProgressPercent(downloadProgress.processedDays, downloadProgress.totalDays);
  const progressLabel = downloadProgress.totalDays > 0
    ? `${downloadProgress.processedDays} of ${downloadProgress.totalDays} days`
    : 'Starting export...';
  const progressStageLabel = downloadStageLabel(downloadProgress.stage, downloadProgress.status);

  return (
    <div className="h-full w-full *:bg-gray-50" style={isLocal() ? { border: '4px solid red' } : {}}>
      {isLocal() && (
        <div className="bg-red-600 text-red text-center p-2">
          <strong>Local Environment</strong>
        </div>
      )}
      <div className="max-w-7xl mx-auto p-2">
        <div className="bg-card border border-border rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold mb-2">Analyze Filter Change</h2>
          {selectedDevice && metadataLoaded && !oldestDate && !latestDate && (
            <p role="status" className="mb-4 rounded-lg border border-border bg-muted p-3 text-sm">
              No analysis metadata is available for this device yet. You can still run an analysis to see raw readings.
            </p>
          )}

          {/* FS-667: the selected device's open alerts, so a quick review here
              does not miss a humidity or runtime alert. It follows the form's
              selection — not the pins, which can be other devices — and gates
              itself on read_alerts. */}
          {selectedDevice && <OpenAlertsBox device={selectedDevice} />}

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Column 1: Analysis method and device details */}
              <div className="flex flex-col gap-4">
                {/* Plan 80: analysis selector — shown only when the device has
                    more than the single standard analysis. */}
                {selectedDevice && availableAnalyses.length > 1 && (
                  <div>
                    <label htmlFor="analysis" className="block text-sm font-medium mb-2">
                      Analysis
                    </label>
                    <Select value={selectedAnalysisKey} items={availableAnalyses.map((analysis) => ({ value: analysis.analysis_key, label: `${analysis.display_name}${analysis.is_default ? ' (default)' : ''}` }))} onValueChange={(value) => { if (value !== null) void handleAnalysisChange(value); }} disabled={isFormDisabled}>
                      <SelectTrigger id="analysis" className="w-full" aria-describedby="analysis-help" title="Choose the analysis method used for these dates"><SelectValue /></SelectTrigger>
                      <SelectContent listLabel="Analysis methods">{availableAnalyses.map((a) => (
                        <SelectItem key={a.analysis_key} value={a.analysis_key}>{a.display_name}{a.is_default ? ' (default)' : ''}</SelectItem>
                      ))}</SelectContent>
                    </Select>
                    <p id="analysis-help" className="text-xs text-muted-foreground">Choose which calculation provides the chart results.</p>
                    {(() => {
                      const sel = availableAnalyses.find((a) => a.analysis_key === selectedAnalysisKey);
                      if (sel && !sel.owns_production_health) {
                        return (
                          <p className="text-xs text-amber-800 mt-1">
                            This analysis does not drive app-facing filter health — pressure shown is
                            for comparison only.
                          </p>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}
                {selectedDevice && canManageAnalyses && (
                  <div className="space-y-2">
                    <p>Analysis methods: {availableAnalyses.map(a => `${a.display_name}${a.analysis_key === defaultAnalysisKey ? ' (default)' : ''}`).join(', ') || 'Loading…'}</p>
                    <Button type="button" variant="secondary" title="Change the analysis methods for this Clip" disabled={isFormDisabled} onClick={() => setMethodsDialogOpen(true)}>Change…</Button>
                    {methodsResult && <p role="status">{methodsResult}</p>}
                    <ClipAnalysisRebuilds key={selectedDevice.id} deviceId={selectedDevice.id}
                      methods={methodRebuilds?.deviceId === selectedDevice.id ? methodRebuilds.methods : undefined}
                      onCompleted={handleRebuildCompleted} />
                  </div>
                )}
                {selectedDevice && (
                  <div className="border border-dashed border-border rounded-md">
                    Pressures:&nbsp;&nbsp;
                    {selectedDevice.x46_fanside ? 'x47 - x46 ' : 'x46 - x47 '}
                    {selectedDevice.calibration_offset < 0.0
                      ? `+ ${Math.abs(selectedDevice.calibration_offset)} Pa`
                      : `- ${selectedDevice.calibration_offset} Pa`}
                  </div>
                )}

                <Button
                  variant="link"
                  size="md"
                  type="button"
                  className="self-start"
                  onClick={handleDownloadCSV}
                  disabled={!selectedDevice || loading || downloadingCsv || !dateRangeValid}
                  title="Download this device's readings for the chosen dates as a CSV file; large ranges take a minute to prepare"
                >
                  {downloadingCsv ? 'Preparing CSV...' : 'Download CSV'}
                </Button>
                {downloadingCsv && (
                  <div className="space-y-1" aria-live="polite">
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span>
                        {progressStageLabel}
                      </span>
                      <span>{progressLabel}</span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                      <div
                        className="h-full rounded-full bg-blue-500 transition-all"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                  </div>
                )}

              </div>

              {/* Column 2: Date Selection and Checkboxes */}
              <div className="space-y-4">

                <FilterDateSelector
                  selectedDevice={selectedDevice?.name || ''}
                  onDateRangeSelect={handleDateRangeSelect}
                  onReadyChange={ready => setPeriodRangeReady(ready ? `${selectedDevice?.id}:${selectedAnalysisKey}` : null)}
                  disabled={isFormDisabled || datesLocked}
                  devices={devices}
                  externalStartDate={startDate}
                  externalEndDate={endDate}
                  datesLocked={datesLocked}
                  // FS-508: periods are bounded by the SELECTED analysis's
                  // own processed days, so switching analysis refetches
                  // them. isFilterPeriod tells the selector whether the
                  // page's range is still its own — clamping and snapping
                  // must not clear the selection, a manual date edit must.
                  analysisKey={selectedAnalysisKey}
                  isFilterPeriod={isFilterPeriod}
                  selectedEpochId={selectedEpochId}
                />

                <div className="space-y-4">
                  <div>
                    <label htmlFor="startDate" className="block text-sm font-medium mb-2">
                      Start Date {datesLocked && <span className="text-xs text-muted-foreground">(locked)</span>}
                    </label>
                    <Input
                      id="startDate"
                      name="startDate"
                      type="date"
                      aria-describedby="start-date-help"
                      value={startDate}
                      onChange={(e) => handleStartDateChange(e.target.value)}
                      disabled={!selectedDevice || isFormDisabled || datesLocked}
                      min={oldestDate}
                      max={endDate || maxSelectableDate}
                    />
                    <p id="start-date-help" className="text-xs text-muted-foreground">First day included in the analysis.</p>
                  </div>
                  <div>
                    <label htmlFor="endDate" className="block text-sm font-medium mb-2">
                      End Date {datesLocked && <span className="text-xs text-muted-foreground">(locked)</span>}
                    </label>
                    <Input
                      id="endDate"
                      name="endDate"
                      type="date"
                      aria-describedby="end-date-help"
                      value={endDate}
                      onChange={(e) => handleEndDateChange(e.target.value)}
                      disabled={!selectedDevice || isFormDisabled || datesLocked}
                      min={startDate || oldestDate}
                      max={maxSelectableDate}
                    />
                    <p id="end-date-help" className="text-xs text-muted-foreground">Last day included in the analysis.</p>
                  </div>
                  {selectedDevice && !loadingOldestDate && !rangeHasData && (
                    <p className="text-xs text-amber-800">
                      No processed data in this date range for the selected
                      device and analysis, so an analysis can&apos;t run.
                      Pick dates inside the available window.
                    </p>
                  )}
                </div>
              </div>

              {/* Column 3: Pin Analysis controls */}
              <div className="space-y-4" role="group" aria-label="Pin Analysis controls">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <Checkbox
                    checked={isPinMode}
                    onCheckedChange={handleTogglePinMode}
                    aria-describedby="pin-analysis-help"
                  />
                  Pin Analysis
                </label>
                <p id="pin-analysis-help" className="text-xs text-muted-foreground">
                  Pin the current date range, then add more devices to compare them on the same window.
                </p>

                <div>
                  <label htmlFor="device" className="block text-sm font-medium mb-2">Device</label>
                  <DeviceSelector
                    name="device"
                    devices={availableDevices}
                    allDevices={devices}
                    separatorBeforeIds={separatorBeforeIds}
                    labelMode="pin"
                    enrichmentMap={enrichmentMap}
                    value={selectedDevice?.name || ''}
                    onChange={handleDeviceChange}
                    className="w-full p-2 border rounded-md"
                    wrapSelectedLabel
                    disabled={!isPinMode}
                    helpText={isPinMode
                      ? 'Choose another Clip to add to the comparison.'
                      : 'Turn on Pin Analysis to add another Clip to the comparison.'}
                  />
                </div>

                {pinnedAnalyses.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Pinned</div>
                    <div className="flex flex-wrap gap-2">
                      {pinnedAnalyses.map((p) => (
                        <span
                          key={`${p.device.id}:${p.analysisKey}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-status-neutral-border bg-status-neutral text-status-neutral-foreground text-xs"
                        >
                          <span
                            className="inline-block w-2 h-2 rounded-full"
                            style={{ backgroundColor: p.color }}
                            aria-hidden
                          />
                          <span>
                            {deviceClipLabel(p.device)}
                            {p.analysisKey !== 'standard_daily_v1' && (
                              <span className="text-muted-foreground"> · {p.analysisLabel}</span>
                            )}
                          </span>
                          <Button
                            variant="icon"
                            size="sm"
                            type="button"
                            onClick={() => handleUnpin(p.device.id, p.analysisKey)}
                            className="ml-1"
                            aria-label={`Unpin ${p.device.name} ${p.analysisLabel}`}
                            title="Take this analysis out of the comparison; you can add it again later"
                          >
                            ×
                          </Button>
                        </span>
                      ))}
                    </div>

                    <div>
                      <label htmlFor="compareOrientation" className="block text-sm font-medium mb-1">
                        Compare charts:
                      </label>
                      <Select value={compareOrientation} items={[{ value: 'vertical', label: 'vertically' }, { value: 'horizontal', label: 'horizontally' }, { value: 'overlaid', label: 'overlaid' }]} onValueChange={(value) => {
                        if (value === 'vertical' || value === 'horizontal' || value === 'overlaid') {
                          setCompareOrientation(value);
                          writeCompareOrientation(value);
                        }
                      }}>
                        <SelectTrigger id="compareOrientation" className="w-full" aria-describedby="compare-help" title="Choose how pinned charts are arranged"><SelectValue /></SelectTrigger>
                        <SelectContent listLabel="Compare charts">
                          <SelectItem value="vertical">vertically</SelectItem>
                          <SelectItem value="horizontal">horizontally</SelectItem>
                          <SelectItem value="overlaid">overlaid</SelectItem>
                        </SelectContent>
                      </Select>
                      <p id="compare-help" className="text-xs text-muted-foreground">Arrange pinned results in separate charts or overlay their series.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Status Messages */}
            <div className="mt-6">
              {error && (
                <div role="alert" className="border border-status-error-border bg-status-error text-status-error-foreground p-3 rounded-md whitespace-pre-line mb-4">
                  {error}
                </div>
              )}

              {success && emptyAnalyses.length === 0 && (
                <div className="border border-status-success-border bg-status-success text-status-success-foreground p-3 rounded-md mb-4">
                  Analysis completed successfully!
                </div>
              )}

              {emptyAnalyses.length > 0 && (
                <div className="border border-status-info-border bg-status-info text-status-info-foreground p-3 rounded-md mb-4">
                  No daily-analysis data yet for{' '}
                  {emptyAnalyses.map((p) => p.device.name).join(', ')}.
                  Freshly installed devices populate after the next nightly processing
                  run (usually within 24 hours of the first full day of readings).
                </div>
              )}

              <Button
                variant="primary"
                type="submit"
                disabled={!selectedDevice || isFormDisabled || !dateRangeValid}
                className="w-full"
                title={isPinMode
                  ? 'Run the chosen analysis for this device and dates and add it to the comparison below'
                  : 'Run the chosen analysis for this device and dates and show the charts'}
              >
                {loading
                  ? isPinMode ? 'Adding...' : 'Analyzing...'
                  : loadingOldestDate
                    ? 'Loading...'
                    : isPinMode ? 'Add Analysis' : 'Start Analysis'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <RequestedAnalysisComparison ready={!!selectedDevice && metadataLoaded && periodRangeReady === `${selectedDevice?.id}:${selectedAnalysisKey}` && !loadingOldestDate && isValidDateRange(startDate, endDate)} onCompare={key => void handleOpenComparison(key)} />
      {methodsDialogOpen && selectedDevice && <ClipAnalysisMethodsDialog deviceId={selectedDevice.id} onClose={() => setMethodsDialogOpen(false)} onSaved={(message, result) => void handleMethodsSaved(message, result)} />}
      {pinnedAnalyses.length > 0 && (
        <PressureAnalysisDisplay
          analyses={pinnedAnalyses}
          startDate={startDate}
          endDate={endDate}
          isFilterPeriod={isFilterPeriod}
          orientation={compareOrientation}
          enrichmentMap={enrichmentMap}
        />
      )}
    </div>
  );
};

export default AnalyzePanel;
